import {
  Box,
  Flex,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text
} from '@chakra-ui/react';
import React from 'react';

import type { CaseSpecialtyData } from '../../interface/caseSpecialty';
import type { SlotProps } from '../../interface/slotProps';

const na = (v: unknown): string =>
  typeof v === 'string' || typeof v === 'number' ? String(v) : 'N/A';

const INTER_STACK = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const HOVER_HINT_SX = {
  cursor: 'help',
  textDecoration: 'underline dotted var(--border-strong, #c9c8ce)',
  textUnderlineOffset: '3px',
  '&:hover, &:focus-visible': {
    textDecorationColor: 'var(--text-muted, #7a7682)'
  }
} as const;

interface CaseSpecialtyRow {
  readonly id: string;
  readonly label: string;
  readonly value?: string;
}

const CaseSpecialtyRowView: React.FC<{ readonly row: CaseSpecialtyRow }> = ({
  row
}) => (
  <Box display="contents" data-testid={`case-specialty-${row.id}`}>
    <Text
      as="span"
      fontSize="11px"
      fontWeight={400}
      color="#4a4751"
      lineHeight="1.6"
      whiteSpace="nowrap"
    >
      {row.label}
    </Text>
    <Text
      as="span"
      fontSize="11.5px"
      fontWeight={600}
      color="#1a1818"
      lineHeight="1.6"
      whiteSpace="nowrap"
    >
      {na(row.value)}
    </Text>
  </Box>
);

const ROLE_PRIMARY = {
  bg: 'var(--ai-faint, #efeafc)',
  color: 'var(--ai, #6b4bd4)'
} as const;

const ROLE_NEUTRAL = {
  bg: 'var(--panel-2, #eceef2)',
  color: 'var(--text-2, #5b5966)'
} as const;

const ServiceRoleBadge: React.FC<{ readonly role: string }> = ({ role }) => {
  const palette =
    role.toLowerCase() === 'primary' ? ROLE_PRIMARY : ROLE_NEUTRAL;
  return (
    <Box
      as="span"
      data-testid="case-specialty-role"
      fontFamily={INTER_STACK}
      fontSize="9px"
      fontWeight={700}
      textTransform="uppercase"
      letterSpacing="0.07em"
      lineHeight="1.2"
      borderRadius="3px"
      px="5px"
      py="2px"
      flexShrink={0}
      bg={palette.bg}
      color={palette.color}
    >
      {role.toUpperCase()}
    </Box>
  );
};

export const CaseSpecialty: React.FC<SlotProps> = ({ value, mode }) => {
  const data: CaseSpecialtyData =
    value != null && typeof value === 'object' && !Array.isArray(value)
      ? (value as CaseSpecialtyData)
      : {};

  if (mode === 'grading') {
    return (
      <Text
        as="span"
        data-testid="case-specialty"
        aria-label={`Service line: ${na(data.serviceLine)}`}
        fontFamily={INTER_STACK}
        fontSize="12px"
        fontWeight={500}
        color="#1a1818"
      >
        {na(data.serviceLine)}
      </Text>
    );
  }

  const rows: readonly CaseSpecialtyRow[] = [
    { id: 'specialty', label: 'Case specialty', value: data.specialty },
    { id: 'department', label: 'Department', value: data.department },
    { id: 'billing-area', label: 'Billing area', value: data.billingArea }
  ];

  const serviceRole =
    typeof data.serviceRole === 'string' && data.serviceRole.trim() !== ''
      ? data.serviceRole
      : undefined;

  return (
    <Flex as="span" align="center" gap="6px">
      <Popover trigger="hover" placement="bottom-start">
        <PopoverTrigger>
          <Text
            as="span"
            tabIndex={0}
            aria-label={`Case specialty: ${na(data.specialty)}`}
            data-testid="case-specialty"
            fontFamily={INTER_STACK}
            fontSize="12px"
            fontWeight={500}
            color="#1a1818"
            sx={HOVER_HINT_SX}
          >
            {na(data.specialty)}
          </Text>
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            data-testid="case-specialty-popover"
            bg="#f9f8fa"
            border="1px solid #ececef"
            borderRadius="9px"
            boxShadow="0 14px 36px -14px rgba(0,0,0,0.4), 0 2px 8px -4px rgba(0,0,0,0.18)"
            padding="11px 13px"
            width="max-content"
            minWidth="226px"
            maxWidth="360px"
            fontFamily={INTER_STACK}
          >
            <Box
              display="grid"
              gridTemplateColumns="2fr 2fr 1fr"
              columnGap="14px"
              rowGap="2px"
              alignItems="start"
            >
              {rows.map((row) => (
                <CaseSpecialtyRowView key={row.id} row={row} />
              ))}
              <Box
                as="button"
                type="button"
                data-testid="case-specialty-edit"
                aria-label="Edit case specialty"
                title="Edit case specialty"
                onClick={() => {
                  /* No-op for now (a later story wires the edit action) */
                }}
                gridColumn="3"
                gridRow={`1 / span ${rows.length}`}
                justifySelf="end"
                alignSelf="start"
                background="none"
                border="1px solid var(--border-strong, #c9c8ce)"
                borderRadius="5px"
                px="6px"
                py="2px"
                fontSize="13px"
                lineHeight="1.6"
                color="var(--text-muted, #7a7682)"
                cursor="pointer"
                _hover={{
                  borderColor: 'var(--primary, #6b4bd4)',
                  color: 'var(--primary, #6b4bd4)',
                  background: 'var(--ai-faint, #efeafc)'
                }}
              >
                ✏
              </Box>
            </Box>
          </PopoverContent>
        </Portal>
        {serviceRole && <ServiceRoleBadge role={serviceRole} />}
      </Popover>
    </Flex>
  );
};
